import React, { useEffect, useRef, useState, useMemo } from "react"
import { useSelector } from "react-redux"
import { get, merge, includes } from "lodash"

import {
  BarChart,
  BarChartDataType,
  BarChartOptionType,
} from "components/utils/chart"
import { useHistogram } from "components/utils/useChart"
import { APIResponse } from "redux/apis/types"
import ReactResizeDetector from "react-resize-detector"
import uuid from "uuid"
import ClassNames from "classnames"

type BarChartComponentType = {
  data: BarChartDataType
  option?: BarChartOptionType
}

export const BarChartComponent: React.FC<BarChartComponentType> = props => {
  // const colorSetting = useSelector(state => get(state, "Setting.color"))
  // const target = useRef<HTMLDivElement>(null)
  // const [chartObject, setChartObject] = useState<BarChart>(null)
  // useEffect(() => {
  //   const option = merge(
  //     {
  //       width: get(target.current, "clientWidth"),
  //     },
  //     props.option
  //   )
  //   if (props.data) {
  //     if (!chartObject) {
  //       const chart = new BarChart(props.data, target.current, "", option)
  //       setChartObject(chart)
  //     } else {
  //       chartObject.redraw(option)
  //     }
  //   }
  // }, [props.data, colorSetting])
  // return <div ref={target} />
  const className = useMemo(() => {
    return `histogram-${uuid()}`
  }, [])
  const displayData: BarChartDataType = props.data.map(data => {
    return {
      name: `${get(data, "Nendo")}/${get(data, "Ki")}`,
      volume: get(data, "value"),
    }
  })
  const element = document.getElementsByClassName(className)
  const width = get(element, [0, "offsetWidth"], 200)

  const createHistogram = useHistogram({
    entryPoint: `.${className}`,
    dataset: displayData,
    option: {
      width: width || 200,
      height: 200,
      padding: [30, 0, 50, 0],
      label_rotate: {
        rotate: -45,
        x: -10,
        y: 10,
      },
      y_ticks: 5,
      notTooltip: true,
    },
  })

  useEffect(() => {
    if (!props.data || !width) return
    createHistogram()
  }, [props.data, width])
  return <div className={ClassNames("histogram", className)} />
}

type PerformanceHistoryBarType = {
  className?: string
  data: Array<APIResponse.Res>
  list: Array<{ name: string; value: string }>
}
type PerformanceCompareBarType = {
  className?: string
  data: APIResponse.Res
  list: Array<{ name: string; value: string; compare: string }>
  noneLabelDiagonal?: boolean
}

type CompareBarType = {
  data: APIResponse.Res
  param: {
    name: string
    value: string
    compare: string
  }
  width: number
  noneLabelDiagonal?: boolean
}

const HistoryBar: React.FC<{
  key?: number
  data: Array<APIResponse.Res>
  param: { name: string; value: string }
  width: number
  noneLabelDiagonal?: boolean
}> = props => {
  // const [chart, setChart] = useState<BarChart>(null)
  // const item = useRef<HTMLDivElement>(null)
  const className = useMemo(() => {
    return `histogram-${uuid()}`
  }, [])
  const displayData: BarChartDataType = props.data.map(data => {
    const isYosou = get(data, "YosouKubun")
    const name = isYosou
      ? `予想(${get(data, "KessanTsuki")}月)`
      : `${get(data, "KessanNen")}/${get(data, "KessanTsuki")}`
    return {
      name: name,
      volume: get(data, props.param.value),
    }
  })
  const createHistogram = useHistogram({
    entryPoint: `.${className}`,
    dataset: displayData,
    title: props.param.name,
    option: {
      width: props.width,
      height: 200,
      padding: [30, 0, 50, 0],
      label_rotate: {
        rotate: -45,
        x: -10,
        y: 10,
      },
      y_ticks: 5,
      notTooltip: true,
    },
  })

  useEffect(() => {
    if (!props.data || !props.width) return
    createHistogram()
    // if (!!item.current && !!props.width && !!displayData.length) {
    //   const option: BarChartOptionType = {
    //     width: props.width,
    //     height: 200,
    //     padding: [72, 30],
    //   }
    //   if (!chart) {
    //     setChart(
    //       new BarChart(
    //         displayData,
    //         item.current,
    //         props.param.name,
    //         option,
    //         props.noneLabelDiagonal
    //       )
    //     )
    //   } else {
    //     chart.redraw(option, displayData, props.param.name)
    //   }
    // }
  }, [props.data, props.width])
  return <div className={ClassNames("histogram", className)} />
  // return <div ref={item} />
}
export const PerformanceHistoryBar: React.FC<
  PerformanceHistoryBarType
> = props => {
  const container = useRef<HTMLDivElement>(null)

  return (
    <ReactResizeDetector
      handleWidth={true}
      render={() => {
        const barWidth: number = !container.current
          ? 0
          : container.current.offsetWidth / props.list.length
        return (
          <div
            ref={container}
            className={props.className}
            style={{ display: "flex" }}
          >
            {props.list.map((lst, i) => (
              <HistoryBar
                key={i}
                data={props.data}
                param={lst}
                width={barWidth}
              />
            ))}
          </div>
        )
      }}
    />
  )
}

const CompareBar: React.FC<CompareBarType> = props => {
  const className = useMemo(() => {
    return `histogram-${uuid()}`
  }, [])
  const [mainValue, compareValue] = useMemo(() => {
    const mainParam = get(props.param, "value")
    const compareParam = get(props.param, "compare")
    return [get(props.data, mainParam), get(props.data, compareParam)]
  }, [props.data, props.param])

  const displayData: BarChartDataType = [
    {
      name: "",
      volume: mainValue,
    },
    {
      name: "業界平均",
      volume: compareValue,
    },
  ]
  const createHistogram = useHistogram({
    entryPoint: `.${className}`,
    dataset: displayData,
    title: props.param.name,
    option: {
      width: props.width - 4,
      height: 200,
      padding: [30, 0, 50, 0],
      y_ticks: 5,
      notTooltip: true,
    },
  })
  useEffect(() => {
    if (!props.data || !props.width) return
    createHistogram()
  }, [props.data, props.width])

  return <div className={className} />
}

export const PerformanceCompareBar: React.FC<
  PerformanceCompareBarType
> = props => {
  const container = useRef<HTMLDivElement>(null)
  return (
    <ReactResizeDetector
      handleWidth={true}
      render={() => {
        const barWidth: number = !container.current
          ? 0
          : container.current.offsetWidth / props.list.length
        return (
          <div
            ref={container}
            className={props.className}
            style={{ display: "flex" }}
          >
            {props.list.map((lst, i) => (
              <CompareBar
                key={i}
                data={props.data}
                param={lst}
                width={barWidth}
                noneLabelDiagonal={props.noneLabelDiagonal}
              />
            ))}
          </div>
        )
      }}
    />
  )
}
